import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import DatePicker from 'react-datepicker';
import { ApiBaseUrlContext } from '../App';
import { ReactComponent as AddIcon } from '../assets/svg/icon-add.svg';
import 'react-datepicker/dist/react-datepicker.css';
import './NotificationInput.css';
import '../boxitem.css';
import BoxItem from './BoxItem';
import { useTranslation } from 'react-i18next';
import GroupedDataTable from './GroupDataTable2'; 

const NotificationInput = () => {
    const apiBaseUrl = useContext(ApiBaseUrlContext);
    const [showContent, setShowContent] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [header, setHeader] = useState('');
    const [text, setText] = useState('');
    const [startTime, setStartTime] = useState(new Date());
    const [endTime, setEndTime] = useState(new Date());
    const [isEditing, setIsEditing] = useState(false);
    const [currentId, setCurrentId] = useState(null); // This will track the ID for editing
    const { t } = useTranslation();

    // Fetch notifications from the server
    const fetchNotifications = useCallback(async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}/mnt/api/notificationinput`);
            if (Array.isArray(response.data)) {
                setNotifications(response.data);
            } else {
                setNotifications([]);
            }
        } catch (error) {
            console.error('Error fetching notifications', error);
        }
    }, [apiBaseUrl]); // Memoize with useCallback to avoid recreating the function on every render
    
    useEffect(() => {
        fetchNotifications();
    }, [fetchNotifications]);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        const notificationData = {
            header,
            text,
            start_time: startTime.getTime(),  // Convert to milliseconds
            end_time: endTime.getTime(),      // Convert to milliseconds
        };
    
        try {
            if (isEditing && currentId) {
                // Update existing notification if editing
                await axios.put(`${apiBaseUrl}/mnt/api/notificationinput/${currentId}`, notificationData);
                alert('Notification updated successfully');
            } else {
                // Add a new notification
                await axios.post(`${apiBaseUrl}/mnt/api/notificationinput`, notificationData);
                alert('Notification added successfully');
            }
            resetForm(); // Reset form after submit
            fetchNotifications(); // Refresh the notifications list
        } catch (error) {
            console.error('Error submitting notification', error);
        }
    };

    // Handle edit action
    /*
    const handleEdit = (notification) => {
        setHeader(notification.header);
        setText(notification.text);
        setStartTime(new Date(notification.start_time));
        setEndTime(new Date(notification.end_time));
        setCurrentId(notification.id); // Set the currentId for editing
        setIsEditing(true);

        setShowForm(true);
        setShowContent(false);
    };*/

    const handleEdit = (notification) => {
      setHeader(notification.header);
      setText(notification.text);
      setStartTime(new Date(notification.start_time));
      setEndTime(new Date(notification.end_time));
      setCurrentId(notification.id);
      setIsEditing(true);
      setShowForm(true);
      setShowContent(false);
    };

    // Handle delete action
    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this notification?')) {
            try {
                await axios.delete(`${apiBaseUrl}/mnt/api/notificationinput/${id}`);
                alert('Notification deleted successfully');
                fetchNotifications(); // Refresh the list
            } catch (error) {
                console.error('Error deleting notification', error);
            }
        }
    };

    // Reset form fields
    const resetForm = () => {
        setHeader('');
        setText('');
        setStartTime(new Date());
        setEndTime(new Date());
        setIsEditing(false);
        setCurrentId(null); // Reset currentId when form is reset

        setShowForm(false);
        setShowContent(true);        
    };

    return (

        <div>


        {showForm && (
            
            <div className="modal-full">

                <form onSubmit={handleSubmit}>

                    <div className="top-menu">
                      <button type="submit" className="button--blue">{isEditing ?  t('notifications.update') : t('notifications.submit')}</button>
                      <div className="top-menu__button-space"></div>
                      <button type="button" className="button--grey" onClick={resetForm}> {t('notifications.cancel')}</button>
                    </div>

                    <div className="content__main-title">{isEditing ? t('notifications.editTitle') : t('notifications.addTitle')}</div>

                    <div className="form-section">

                        <div className="entry-item entry-item entry-item__full-width">
                            <label className="entry-label">{t('notifications.title')}</label>         
                            <input
                                value={header}
                                className="entry-control"
                                type="text"
                                onChange={(e) => setHeader(e.target.value)}
                                required
                            />
                        </div>

                        <div className="entry-item entry-item entry-item__full-width">
                            <label className="entry-label">{t('notifications.description')}</label>
                            <textarea
                                value={text}
                                className="form-entry__textarea" 
                                onChange={(e) => setText(e.target.value)}
                                required
                            />
                        </div>

                        <div className="entry-item entry-item__datetime">
                            <label className="entry-label">{t('notifications.startTime')}</label>
                            <DatePicker
                                className="form-datepicker"
                                selected={startTime}
                                onChange={(date) => setStartTime(date)}
                                showTimeSelect
                                dateFormat="Pp"
                                required
                            />
                        </div>

                        <div className="entry-item entry-item__datetime">
                            <label className="entry-label">{t('notifications.endTime')}</label>
                            <DatePicker
                                className="form-datepicker"
                                selected={endTime}
                                onChange={(date) => setEndTime(date)}
                                showTimeSelect
                                dateFormat="Pp"
                                required
                            />
                        </div>

                    </div>

                    <div className="form-submit">
                        <button type="submit" className="button--blue">
                            {isEditing ? t('notifications.update') : t('notifications.submit')}
                        </button>
                        <div className="top-menu__button-space"></div>
                        <button type="button" className="button--grey" onClick={resetForm}>
                            {t('notifications.cancel')}
                        </button>
                    </div>
                    
                </form>

            </div>

        )}  

        {showContent && (

            <div className="content">
                <div className="top-menu">
                    <div className="top-menu__divider"></div>
                    <button
                        type="button"
                        className="button--icon-add button--green"
                        onClick={() => {
                        setShowForm(true);
                        setShowContent(false);
                    }}
                    >
                        <AddIcon /> {t('notifications.addNotification')}
                    </button>
                </div>

                <div className="content__main-title">{t('notifications.sectionTitle')}</div>

                    <GroupedDataTable
                      groupedData={{
                        active: notifications, // Use "active" or group by date/type if needed
                      }}
                      isFiltered={false}
                      headers={[
                        {
                          label: t("notifications.title"),
                          accessor: "header",
                          elasticCell: true,
                          colorStyle: "block",
                          colorValueFrom: () => "grey",
                        },
                        {
                          label: t("notifications.from"),
                          render: (item) => new Date(item.start_time).toLocaleString(),
                          hideMobile: true,
                        },
                        {
                          label: t("notifications.to"),
                          render: (item) => new Date(item.end_time).toLocaleString(),
                          hideMobile: true,
                        },
                        {/*
                        {
                          label: t("notifications.description"),
                          accessor: "text",
                          hideMobile: true,
                        },
                        */}
                      ]}
                      rowKeyFn={(item) => item.id}
                      getPrimaryActionLabel={() => t("notifications.edit")}
                      onPrimaryAction={(item) => handleEdit(item)}
                      groupMeta={{
                        active: {
                          title: t("notifications.allNotifications"),
                          description: t("notifications.sectionDescription"),
                          emptyMessage: t("notifications.noneAvailable"),
                        },
                      }}
                      contextualOptionsFn={(item) => [
                        {
                          label: t("notifications.delete"),
                          onClick: (e) => {
                            e.preventDefault();
                            handleDelete(item.id);
                          },
                        },
                      ]}
                    />

                </div>
            )} 

        </div>

    );
};

export default NotificationInput;
